import { useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  CornerDownRight,
  Flag,
  Loader2,
  MessageSquare,
  Phone,
  Send,
  Trash2,
} from 'lucide-react'

import { ReportProblemDialog } from '@/components/ReportProblemDialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { PushToTalkMic } from '@/components/voice/PushToTalkMic'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatPhone } from '@/lib/format'
import { formatDateTime, formatRelative } from '@/lib/dates'

/**
 * Eric — multi-channel inbound, member-side.
 *
 * Per-user page: every reader role can register their own phone (and
 * future channels) to text Eric on the go. The Worker scopes inbound
 * requests by sender identity → shop, so a registered mechanic on a
 * specific phone gets shop-scoped access to Eric's tools.
 *
 * v1 surfaces SMS only. WhatsApp / Telegram / Messenger are wired in
 * v2 / v3 and will appear as additional cards in the channel picker.
 */

type Identity = {
  id: string
  channel: string
  channel_user_id: string
  verified_at: string
  created_at: string
}

type IdentitiesResponse = {
  identities: Identity[]
}

type HistoryEntry = {
  id: string
  channel: string
  request_text: string
  guardrail_decision: string
  final_reply: string | null
  error: string | null
  ms_total: number | null
  created_at: string
}

type HistoryResponse = {
  entries: HistoryEntry[]
}

export function Messaging() {
  const { t } = useTranslation('messaging')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<Identity | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [resumeTarget, setResumeTarget] = useState<HistoryEntry | null>(null)

  const identitiesQuery = useQuery({
    queryKey: ['messaging', 'identities'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/messaging/identities', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as IdentitiesResponse
    },
  })

  // Pull this user's recent Eric conversation (in_app + sms combined),
  // server-side scoped to (shop_id, clerk_user_id). Cap to 20 — beyond
  // that the on-page list gets unwieldy and admins have the full
  // cross-tenant log anyway.
  const historyQuery = useQuery({
    queryKey: ['messaging', 'eric-history'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/eric/history?limit=20', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as HistoryResponse
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/messaging/identities/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messaging', 'identities'] })
      setPendingDelete(null)
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('header.subtitle')}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setReportOpen(true)}
        >
          <Flag className="size-3.5" />
          {t('report_problem')}
        </Button>
      </div>

      <Card className="rounded-[14px] ring-border p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              {t('identities.header')}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('identities.subtitle')}
            </p>
          </div>
          <Button type="button" onClick={() => setAddOpen(true)}>
            {t('identities.add')}
          </Button>
        </div>

        {identitiesQuery.isLoading ? (
          <div className="mt-4 text-sm text-muted-foreground">
            {tCommon('loading')}
          </div>
        ) : identitiesQuery.isError ? (
          <div className="mt-4 rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {identitiesQuery.error instanceof Error
              ? identitiesQuery.error.message
              : t('identities.error')}
          </div>
        ) : (identitiesQuery.data?.identities.length ?? 0) === 0 ? (
          <div className="mt-4 rounded-[14px] border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
            {t('identities.empty')}
          </div>
        ) : (
          <div className="mt-4 grid gap-3 grid-cols-1 md:grid-cols-2">
            {identitiesQuery.data!.identities.map((id) => (
              <div
                key={id.id}
                className="flex items-start justify-between gap-3 rounded-[10px] border border-border bg-background p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <ChannelIcon channel={id.channel} />
                    <span className="text-[12px] font-semibold text-muted-foreground">
                      {t(`channels.${id.channel}`, id.channel)}
                    </span>
                  </div>
                  <div className="mt-1 font-mono text-sm text-foreground">
                    {id.channel === 'sms' || id.channel === 'whatsapp'
                      ? formatPhone(id.channel_user_id)
                      : id.channel_user_id}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t('identities.verified_on', {
                      date: formatDateTime(id.verified_at),
                    })}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setPendingDelete(id)}
                  aria-label={t('identities.unregister')}
                  className="min-h-11 min-w-11"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <HistoryCard
        entries={historyQuery.data?.entries ?? []}
        loading={historyQuery.isLoading}
        error={historyQuery.isError}
        onContinue={(entry) => setResumeTarget(entry)}
      />

      <ContinueConversationDialog
        // Keying on the target id forces a remount when the user picks a
        // different entry to resume — thread state seeds from the new
        // entry without needing a setState-in-effect dance.
        key={resumeTarget?.id ?? 'closed'}
        target={resumeTarget}
        onClose={() => setResumeTarget(null)}
        onReplyPersisted={() => {
          qc.invalidateQueries({ queryKey: ['messaging', 'eric-history'] })
        }}
      />

      <AddChannelDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ['messaging', 'identities'] })
          setAddOpen(false)
        }}
      />

      <UnregisterDialog
        identity={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id)
        }}
        deleting={deleteMutation.isPending}
      />

      <ReportProblemDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Past conversations with Eric — read-only, scoped to the current user.

function HistoryCard({
  entries,
  loading,
  error,
  onContinue,
}: {
  entries: HistoryEntry[]
  loading: boolean
  error: boolean
  onContinue: (entry: HistoryEntry) => void
}) {
  const { t } = useTranslation('messaging')
  const { t: tCommon } = useTranslation('common')

  return (
    <Card className="rounded-[14px] ring-border p-5">
      <div>
        <h2 className="text-sm font-semibold text-foreground">
          {t('history.header')}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('history.subtitle')}
        </p>
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-muted-foreground">
          {tCommon('loading')}
        </div>
      ) : error ? (
        <div className="mt-4 rounded-[10px] border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {t('history.error')}
        </div>
      ) : entries.length === 0 ? (
        <div className="mt-4 rounded-[14px] border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          {t('history.empty')}
        </div>
      ) : (
        <ol className="mt-4 space-y-3">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="rounded-[10px] border border-border bg-background p-3"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <ChannelIcon channel={entry.channel} />
                  <span>
                    {t(`channels.${entry.channel}`, entry.channel)}
                  </span>
                </span>
                <span title={formatDateTime(entry.created_at)}>
                  {formatRelative(entry.created_at)}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="rounded-md bg-muted/40 p-2 text-foreground">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {t('history.you')}:
                  </span>{' '}
                  {entry.request_text}
                </div>
                {entry.final_reply ? (
                  <div
                    className={
                      entry.error
                        ? 'rounded-md bg-destructive/10 p-2 text-destructive'
                        : 'rounded-md bg-muted/60 p-2 text-foreground'
                    }
                  >
                    <span className="text-xs font-semibold text-muted-foreground">
                      {t('history.eric')}:
                    </span>{' '}
                    {entry.final_reply}
                  </div>
                ) : (
                  <div className="rounded-md bg-muted/30 p-2 text-xs italic text-muted-foreground">
                    {t('history.no_reply', { decision: entry.guardrail_decision })}
                  </div>
                )}
              </div>
              {/* Only offer Continue when Eric actually replied — there's
                  nothing useful to resume from a silent guardrail drop or
                  an in-flight error row. */}
              {entry.final_reply && !entry.error ? (
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onContinue(entry)}
                    className="min-h-11 px-2 text-xs text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
                  >
                    <CornerDownRight className="size-3.5" />
                    {t('history.continue')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </Card>
  )
}

function ChannelIcon({ channel }: { channel: string }) {
  if (channel === 'sms') return <Phone className="size-3.5 text-muted-foreground" />
  return <MessageSquare className="size-3.5 text-muted-foreground" />
}

// ─────────────────────────────────────────────────────────────────────
// Continue a past conversation — opens a chat-style dialog seeded with
// the entry the user clicked. New messages POST /api/eric/ask with
// resume_from_request_id set to the target row, so the server-side
// brain bypasses its 30-min session window and pulls a deeper trail
// from that thread.

type ThreadEntry =
  | { kind: 'user'; text: string; at: number }
  | { kind: 'eric'; text: string; at: number; error: boolean }

type AskResponse = {
  reply: string
  guardrail_decision: string
  tool_calls: unknown[]
  ms_total: number
  error: string | null
}

function ContinueConversationDialog({
  target,
  onClose,
  onReplyPersisted,
}: {
  target: HistoryEntry | null
  onClose: () => void
  onReplyPersisted: () => void
}) {
  const { t } = useTranslation('messaging')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const [text, setText] = useState('')
  // Per-conversation memory key. The parent keys this dialog on target.id, so a
  // fresh id is minted each time a different past entry is continued — isolating
  // each continued conversation's Mastra memory instead of sharing the operator's
  // clerkUserId thread. See worker buildMemoryKey.
  const [conversationId] = useState(() => crypto.randomUUID())
  // Seed the dialog's local thread with the target entry (the turn
  // that motivated the resume). The server loads the deeper history
  // Eric actually uses for context; we don't render it here to keep
  // the dialog focused on the continuation moment.
  // The parent keys this component on target.id, so a target change
  // remounts and re-runs this initializer — no useEffect needed.
  const [thread, setThread] = useState<ThreadEntry[]>(() => {
    if (target && target.final_reply) {
      const at = Date.parse(target.created_at) || Date.now()
      return [
        { kind: 'user', text: target.request_text, at },
        { kind: 'eric', text: target.final_reply, at, error: false },
      ]
    }
    return []
  })
  // Scroll the inner thread container only — `scrollIntoView` on a
  // sentinel cascades up to whatever the nearest scroll ancestor is,
  // which inside a Radix dialog ends up jumping the entire dialog
  // content. Setting scrollTop on the overflow-y-auto box itself keeps
  // the scroll local.
  const threadContainerRef = useRef<HTMLDivElement | null>(null)
  const scrollThreadToBottom = () => {
    const el = threadContainerRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }

  const ask = useMutation({
    mutationFn: async (prompt: string): Promise<AskResponse> => {
      if (!target) throw new Error('no_target')
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/eric/ask', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: prompt,
          resume_from_request_id: target.id,
          conversation_id: conversationId,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null as unknown)
        const code =
          body && typeof body === 'object' && 'error' in body
            ? String((body as { error: unknown }).error)
            : null
        if (res.status === 429) throw new Error(t('continue_dialog.error.rate_limited'))
        if (code === 'invalid_body') throw new Error(t('continue_dialog.error.invalid_body'))
        throw new Error(t('continue_dialog.error.generic'))
      }
      return (await res.json()) as AskResponse
    },
    onSuccess: (data) => {
      setThread((prev) => [
        ...prev,
        { kind: 'eric', text: data.reply, at: Date.now(), error: data.error !== null },
      ])
      onReplyPersisted()
      requestAnimationFrame(scrollThreadToBottom)
    },
    onError: (err) => {
      setThread((prev) => [
        ...prev,
        { kind: 'eric', text: err instanceof Error ? err.message : String(err), at: Date.now(), error: true },
      ])
      requestAnimationFrame(scrollThreadToBottom)
    },
  })

  const submit = () => {
    const trimmed = text.trim()
    if (trimmed.length === 0 || ask.isPending) return
    setThread((prev) => [...prev, { kind: 'user', text: trimmed, at: Date.now() }])
    setText('')
    ask.mutate(trimmed)
    requestAnimationFrame(scrollThreadToBottom)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      submit()
    }
  }

  const open = target !== null

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('continue_dialog.title')}</DialogTitle>
          <DialogDescription>
            {target
              ? t('continue_dialog.body', { when: formatRelative(target.created_at) })
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={threadContainerRef}
          className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-border bg-muted/30 p-3"
        >
          {thread.map((entry, i) => (
            <div
              key={`${entry.at}-${i}`}
              className={
                entry.kind === 'user'
                  ? 'flex flex-col items-end'
                  : 'flex flex-col items-start'
              }
            >
              <div
                className={
                  entry.kind === 'user'
                    ? 'max-w-[85%] rounded-lg bg-foreground px-3 py-2 text-sm text-background'
                    : entry.error
                      ? 'max-w-[85%] rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive'
                      : 'max-w-[85%] rounded-lg bg-background px-3 py-2 text-sm text-foreground shadow-sm'
                }
              >
                {entry.text}
              </div>
              <span
                className="mt-0.5 text-[10px] text-muted-foreground"
                title={formatDateTime(entry.at)}
              >
                {formatRelative(entry.at)}
              </span>
            </div>
          ))}
          {ask.isPending ? (
            <div className="flex justify-start">
              <div className="flex max-w-[85%] items-center gap-2 rounded-lg bg-background px-3 py-2 text-sm text-muted-foreground shadow-sm">
                <Loader2 className="size-3.5 animate-spin" />
                {t('continue_dialog.thinking')}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('continue_dialog.placeholder')}
            rows={2}
            maxLength={500}
            disabled={ask.isPending}
            aria-label={t('continue_dialog.title')}
          />
          <p className="text-xs text-muted-foreground">{t('continue_dialog.hint')}</p>
        </div>

        <DialogFooter>
          <PushToTalkMic
            disabled={ask.isPending}
            onTranscript={(dictated) => {
              setText((prev) => {
                const trimmed = prev.trim()
                if (trimmed.length === 0) return dictated
                return `${trimmed} ${dictated}`
              })
            }}
          />
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={ask.isPending}>
              {t('continue_dialog.close')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={submit}
            disabled={ask.isPending || text.trim().length === 0}
          >
            {ask.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('continue_dialog.sending')}
              </>
            ) : (
              <>
                <Send className="size-4" />
                {t('continue_dialog.send')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Add channel — two-step dialog (enter phone → enter code).

function AddChannelDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const { t } = useTranslation('messaging')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const [step, setStep] = useState<'enter_phone' | 'enter_code'>('enter_phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Reset internal state whenever the dialog closes so reopening
  // doesn't leak stale phone / code values across registrations.
  const reset = () => {
    setStep('enter_phone')
    setPhone('')
    setCode('')
    setError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const startMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/messaging/verify/start', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channel: 'sms',
          channel_user_id: normalizeE164(phone),
        }),
      })
      if (!res.ok) {
        const j = await safeJson(res)
        throw new Error(verifyErrorMessage(j, t, normalizeE164(phone)))
      }
      return res.json()
    },
    onSuccess: () => {
      setError(null)
      setStep('enter_code')
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err))
    },
  })

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/messaging/verify/confirm', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channel: 'sms',
          channel_user_id: normalizeE164(phone),
          code,
        }),
      })
      if (!res.ok) {
        const j = await safeJson(res)
        throw new Error(verifyErrorMessage(j, t, normalizeE164(phone)))
      }
      return res.json()
    },
    onSuccess: () => {
      setError(null)
      reset()
      onSuccess()
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err))
    },
  })

  const submitting = startMutation.isPending || confirmMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {step === 'enter_phone'
              ? t('add_dialog.title_phone')
              : t('add_dialog.title_code')}
          </DialogTitle>
          <DialogDescription>
            {step === 'enter_phone'
              ? t('add_dialog.body_phone')
              : t('add_dialog.body_code', { phone: formatPhone(normalizeE164(phone)) })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {step === 'enter_phone' ? (
            <div className="space-y-1.5">
              <Label htmlFor="messaging-phone">
                {t('add_dialog.phone_label')}
              </Label>
              <Input
                id="messaging-phone"
                type="tel"
                inputMode="tel"
                placeholder="+1 416 555 0100"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={submitting}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                {t('add_dialog.phone_help')}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="messaging-code">
                {t('add_dialog.code_label')}
              </Label>
              <Input
                id="messaging-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                disabled={submitting}
                autoComplete="one-time-code"
              />
              <p className="text-xs text-muted-foreground">
                {t('add_dialog.code_help')}
              </p>
            </div>
          )}

          {error ? <div className="text-xs text-destructive">{error}</div> : null}
        </div>

        <DialogFooter>
          {step === 'enter_code' ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setStep('enter_phone')
                setCode('')
                setError(null)
              }}
              disabled={submitting}
            >
              {t('add_dialog.back')}
            </Button>
          ) : null}
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={submitting}>
              {t('add_dialog.cancel')}
            </Button>
          </DialogClose>
          {step === 'enter_phone' ? (
            <Button
              type="button"
              disabled={submitting || !looksLikeE164(normalizeE164(phone))}
              onClick={() => startMutation.mutate()}
            >
              {startMutation.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('add_dialog.sending')}
                </>
              ) : (
                t('add_dialog.send_code')
              )}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={submitting || code.length !== 6}
              onClick={() => confirmMutation.mutate()}
            >
              {confirmMutation.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('add_dialog.verifying')}
                </>
              ) : (
                t('add_dialog.verify')
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function UnregisterDialog({
  identity,
  onClose,
  onConfirm,
  deleting,
}: {
  identity: Identity | null
  onClose: () => void
  onConfirm: () => void
  deleting: boolean
}) {
  const { t } = useTranslation('messaging')
  const display = identity
    ? identity.channel === 'sms' || identity.channel === 'whatsapp'
      ? formatPhone(identity.channel_user_id)
      : identity.channel_user_id
    : ''

  return (
    <Dialog open={identity !== null} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('unregister.title')}</DialogTitle>
          <DialogDescription>
            {t('unregister.body', { display })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={deleting}>
              {t('unregister.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={deleting}
          >
            {deleting ? <Loader2 className="size-4 animate-spin" /> : null}
            {t('unregister.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Helpers

/**
 * Coerce a user-typed phone (1-416-555-0100, (416) 555-0100, etc.)
 * into a strict E.164 string. Strips spaces, dashes, parentheses, dots.
 * If the user already typed a leading `+`, keep it; otherwise assume
 * Canadian and prepend `+1` if the digit count looks like 10. For 11
 * digits starting with 1, prepend `+`.
 *
 * The server validates strict E.164 anyway, so this is a best-effort
 * UX assist. The Zod regex on the server is the authority.
 */
function normalizeE164(input: string): string {
  const stripped = input.trim()
  if (stripped.startsWith('+')) {
    return '+' + stripped.slice(1).replace(/\D/g, '')
  }
  const digits = stripped.replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return digits ? `+${digits}` : ''
}

function looksLikeE164(s: string): boolean {
  return /^\+[1-9]\d{1,14}$/.test(s)
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

/**
 * Map server error envelopes to user-friendly messages. The server
 * returns `{ error: 'expired' | 'code_mismatch' | 'no_pending_code'
 * | 'invalid_body' | 'send_failed' | 'unverified_caller' |
 * 'invalid_to_number' | 'stop_listed' | 'undeliverable' | ... }`
 * from the verify routes. The send-side errors also carry
 * `twilio_code` and `twilio_message` so we can fall back to the raw
 * Twilio text on unknown codes rather than hiding the cause.
 *
 * Eric's number is hard-coded here because there's no public route
 * to read it from — it's a wrangler secret. Keep this in sync with
 * `TWILIO_PHONE_NUMBER` in `wrangler.jsonc`.
 */
const ERIC_PUBLIC_NUMBER = '+1 437 476 0586'

function verifyErrorMessage(
  body: unknown,
  t: ReturnType<typeof useTranslation<'messaging'>>['t'],
  phone: string,
): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const code = String((body as { error: unknown }).error)
    const knownPlain = ['expired', 'code_mismatch', 'no_pending_code', 'send_failed', 'invalid_body', 'undeliverable']
    if (knownPlain.includes(code)) return t(`errors.${code}`)
    if (code === 'unverified_caller') {
      return t('errors.unverified_caller', { phone })
    }
    if (code === 'invalid_to_number') {
      return t('errors.invalid_to_number')
    }
    if (code === 'stop_listed') {
      return t('errors.stop_listed', { eric: ERIC_PUBLIC_NUMBER })
    }
    // Unknown code — fall back to the Twilio message if the server
    // forwarded one, so the cause isn't swallowed entirely.
    const twilioMessage =
      'twilio_message' in body &&
      typeof (body as { twilio_message: unknown }).twilio_message === 'string'
        ? (body as { twilio_message: string }).twilio_message
        : null
    if (twilioMessage && twilioMessage.length > 0) {
      return twilioMessage
    }
  }
  return t('errors.generic')
}
